import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import EditGuestModal from './EditGuestModal';
import ConfirmationModal from '../ConfirmationModal';

interface Guest {
  id: string;
  name: string;
  phone: string;
  guests_count: number;
  message: string | null;
  created_at: string;
}

interface GuestManagementProps {
  guests: Guest[];
  isLoading: boolean;
  onEditGuest: (guestId: string, data: any) => Promise<void>;
  onDeleteGuest: (guestId: string) => Promise<void>;
  setAlert: (title: string, message: string) => void;
}

const GuestManagement: React.FC<GuestManagementProps> = ({
  guests,
  isLoading,
  onEditGuest,
  onDeleteGuest,
  setAlert
}) => {
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleEditClick = (guest: Guest) => {
    setSelectedGuest(guest);
    setShowEditModal(true);
  };

  const handleDeleteClick = (guest: Guest) => {
    setSelectedGuest(guest);
    setShowDeleteModal(true);
  };

  const handleEditSubmit = async (guestId: string, data: any) => {
    try {
      await onEditGuest(guestId, data);
      setShowEditModal(false);
      setSelectedGuest(null);
      setAlert('Sucesso', 'Convidado atualizado com sucesso!');
    } catch (error) {
      setAlert('Erro', 'Erro ao atualizar convidado. Por favor, tente novamente.');
    }
  };

  const handleDelete = async (guestId: string) => {
    try {
      await onDeleteGuest(guestId);
      setShowDeleteModal(false);
      setSelectedGuest(null);
      setAlert('Sucesso', 'Convidado excluído com sucesso!');
    } catch (error) {
      setAlert('Erro', 'Erro ao excluir convidado. Por favor, tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 bg-wedding-beige-100 border-b">
          <h3 className="font-medium text-wedding-blue-800">Lista de Convidados Confirmados</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acompanhantes
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mensagem
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {guests.map((guest) => (
                <tr key={guest.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{guest.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{guest.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{guest.guests_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(guest.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {guest.message || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditClick(guest)}
                        className="p-1 text-wedding-blue-600 hover:bg-wedding-blue-50 rounded-full transition-colors"
                        title="Editar convidado"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(guest)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Excluir convidado"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t">
          <p className="text-sm text-gray-500">
            Total de convidados: <span className="font-medium">{guests.length}</span>
          </p>
          <p className="text-sm text-gray-500">
            Total de pessoas: <span className="font-medium">
              {guests.reduce((acc, guest) => acc + guest.guests_count + 1, 0)}
            </span>
          </p>
        </div>
      </div>

      {showEditModal && selectedGuest && (
        <EditGuestModal
          guest={selectedGuest}
          onClose={() => {
            setShowEditModal(false);
            setSelectedGuest(null);
          }}
          onSubmit={(data) => handleEditSubmit(selectedGuest.id, data)}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Convidado"
        message="Tem certeza que deseja excluir este convidado? Esta ação não pode ser desfeita."
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedGuest(null);
        }}
        onConfirm={() => {
          if (selectedGuest) {
            handleDelete(selectedGuest.id);
          }
        }}
      />
    </>
  );
};

export default GuestManagement;